import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type {
  PatientFileWithGlance,
  PatientSearchResult,
  PatientSort,
} from "@dentacore/shared-types";

import { getPatient, restorePatient, searchPatients } from "../api/patients";
import { useBranchTimezone } from "../api/use-branch-timezone";
import { useAuth } from "../auth/auth-context";
import { useCan } from "../auth/use-can";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { useRingProvider } from "../shell/ring/ring-registry";
import { DuplicatesModal } from "./duplicates";
import { formatPhone } from "../ui/countries";
import { Pager, useHeight } from "../ui/paging";
import { counted } from "./by-day";
import { formatAmounts } from "./money";
import { NewPatientForm } from "./new-patient-form";
import { PatientFileView } from "./patient-file";
import styles from "./patients.module.css";

/** How long the search box waits after a keystroke before asking the server. */
const DEBOUNCE_MS = 200;

/**
 * Row heights, which the page size is worked out from (PF4): **a page is as
 * many rows as the list has room for**, so a 1366 × 768 laptop gets shorter
 * pages and neither size gets a scroll bar. The CSS draws rows at exactly
 * these heights.
 */
const ROW_PAGE = 42;
const ROW_DRAWER = 34;
/** A drawer row carries a second line while the list is sorted by attendance. */
const ROW_DRAWER_TALL = 50;

/**
 * The patients screen (PF3, 2026-09-27).
 *
 * **`/patients` is the list, at full width** — search, sort, New patient,
 * Removed files and numbered pages. **`/patients/:id` is one file, with no list
 * beside it**: *Patients* above the name opens the same list as a drawer, and
 * the top bar's search reaches anyone from anywhere. This reverses the mockup's
 * always-open 250px rail — *"I am not a big fan of the list of all patients
 * being visible at all times."*
 *
 * The URL carries which patient is open, so a clinician can send a colleague a
 * link to a file and a reload does not lose their place.
 */
export function PatientsPage(): React.JSX.Element {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const [isAdding, setIsAdding] = useState(false);
  const [drawer, setDrawer] = useState(false);

  // Opening another file is the end of choosing one.
  useEffect(() => {
    setDrawer(false);
  }, [patientId]);

  // A dialog over whatever is showing — the list, or the file whose drawer it
  // was opened from — so closing it puts somebody back where they were.
  const adding = isAdding && (
    <NewPatientForm
      onCreated={(patient) => {
        setIsAdding(false);
        navigate(`/patients/${patient.id}`);
      }}
      onCancel={() => setIsAdding(false)}
    />
  );

  if (patientId === undefined) {
    return (
      <div className={styles.screen}>
        <PatientList mode="page" onNew={() => setIsAdding(true)} />
        {adding}
      </div>
    );
  }

  return (
    <div className={styles.screen}>
      {drawer && (
        <>
          <button
            type="button"
            className={styles.scrim}
            aria-label="Close the patient list"
            tabIndex={-1}
            onClick={() => setDrawer(false)}
            data-card=""
            data-testid="patient-drawer-scrim"
          />
          <aside className={styles.drawer} aria-label="Patients" data-testid="patient-drawer">
            <PatientList
              mode="drawer"
              activeId={patientId}
              onNew={() => {
                setDrawer(false);
                setIsAdding(true);
              }}
              onClose={() => setDrawer(false)}
            />
          </aside>
        </>
      )}

      <div className={styles.fileColumn}>
        <div className={styles.top}>
          {/*
            **The drawer on a page with room for it; the list page itself on a
            narrow one**, where a drawer would be the whole screen anyway and
            the back button should bring the file back.
          */}
          {/* An arrow straight back to the patient list, at every width (Anthony,
              2026-10-11). The *Patients* button beside it still opens the drawer. */}
          <Link
            to="/patients"
            className={styles.backArrow}
            aria-label="Back to patient list"
            data-testid="back-arrow"
          >
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
              <path
                d="M10 3 5 8l5 5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
          <button
            type="button"
            className={styles.back}
            aria-expanded={drawer}
            onClick={() => setDrawer((open) => !open)}
            data-testid="open-patient-list"
          >
            <ListIcon />
            Patients
          </button>
          <Link to="/patients" className={styles.backLink} data-testid="back-to-patients">
            <ListIcon />
            Patients
          </Link>
        </div>
        <PatientFileView key={patientId} patientId={patientId} />
      </div>
      {adding}
    </div>
  );
}

function ListIcon(): React.JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path
        d="M2.5 4h11M2.5 8h11M2.5 12h7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * The list — as the page at `/patients`, and as the drawer over a file.
 *
 * One component for both, so the two cannot drift: the same search, the same
 * sort, the same Removed files and the same numbered pages, drawn as a table on
 * the page and as a column of names in the drawer.
 */
function PatientList({
  mode,
  activeId,
  onNew,
  onClose,
}: {
  mode: "page" | "drawer";
  activeId?: string;
  onNew: () => void;
  onClose?: () => void;
}): React.JSX.Element {
  const can = useCan();
  /** Administrators find and merge duplicate files (PP-D13, PP-D14). */
  const administrator = useAuth().user?.canAdminister === true;
  const [duplicates, setDuplicates] = useState(false);
  const navigate = useNavigate();
  const [areaRef, areaHeight] = useHeight<HTMLDivElement>();
  const [sort, setSort] = useState<PatientSort | "">("");
  const rowHeight =
    mode === "page" ? ROW_PAGE : sort === "attendance" ? ROW_DRAWER_TALL : ROW_DRAWER;
  const pageSize = areaHeight <= 0 ? 0 : Math.max(1, Math.floor(areaHeight / rowHeight));
  const list = usePatientList(pageSize, sort);
  const attendance = sort === "attendance";
  const timezone = useBranchTimezone();

  /** Opens a file, as a row's click does: the open file's own row just closes the drawer. */
  const openFile = (patientId: string): void => {
    if (patientId === activeId) onClose?.();
    else navigate(`/patients/${patientId}`);
  };

  /**
   * **A patient's ring** (`patients.row`, STORY-221). The list itself only
   * opens a file, so every other action goes where its button lives: Book to
   * the calendar, as the file's Book does — and, like it, only while nothing is
   * booked (item 17); *Take payment*, *Add a flag* and *Edit details* open the
   * file and press the file's own button there (`?ring=`). The file is read
   * first for what they owe and when they are next in, which the list does not
   * carry.
   */
  useRingProvider("patients.row", async (element) => {
    const patient = list.patients.find((row) => row.id === element.dataset.ringId);
    if (patient === undefined) return null;
    let file: PatientFileWithGlance | null = null;
    try {
      file = await getPatient(patient.id);
    } catch {
      file = null;
    }
    const owed = (file?.outstanding ?? []).filter((amount) => amount.cents > 0);
    const next = file?.nextAppointmentAt ?? null;
    const go = (to: string) => () => {
      onClose?.();
      navigate(to);
    };
    return {
      ring: "patients.row",
      title: `${patient.firstName} ${patient.lastName}`.trim(),
      meta:
        owed.length > 0 && file !== null
          ? `Owes ${formatAmounts(owed, file.currency)}`
          : next !== null
            ? `Next: ${new Date(next).toLocaleString(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
                // The location's clock, as the file's header draws it (P2 Q8).
                timeZone: timezone,
              })}`
            : patient.fileNumberDisplay,
      patientId: patient.id,
      subject: { type: "patient", id: patient.id },
      actions: [
        next === null && {
          key: "book",
          label: "Book appointment",
          icon: RING_ICONS.book,
          hint: "Books their next visit",
          needs: ["appointments.book"],
          run: go(`/calendar?patient=${patient.id}`),
        },
        owed.length > 0 &&
          file !== null && {
            key: "pay",
            label: "Take payment",
            icon: RING_ICONS.pay,
            hint: `They owe ${formatAmounts(owed, file.currency)}`,
            needs: ["billing.view", "billing.payment.record"],
            run: go(`/patients/${patient.id}?tab=Billing&ring=pay`),
          },
        {
          key: "flag",
          label: "Add a flag",
          icon: RING_ICONS.flag,
          hint: "An allergy or a warning",
          needs: ["patients.flags.manage"],
          run: go(`/patients/${patient.id}?ring=flag`),
        },
        {
          key: "edit",
          label: "Edit details",
          icon: RING_ICONS.pencil,
          hint: "Name, date of birth, recall",
          needs: ["patients.edit"],
          run: go(`/patients/${patient.id}?ring=edit`),
        },
        {
          key: "file",
          label: "Open file",
          icon: RING_ICONS.file,
          hint: "Opens their file",
          needs: ["patients.view"],
          run: () => openFile(patient.id),
        },
      ],
    };
  });

  const empty =
    list.error === null && list.patients.length === 0 && !list.loading ? (
      <p className={styles.note} data-testid="patient-list-empty">
        {list.removed
          ? "No removed files. Nothing has been taken out of this clinic's list."
          : list.term.trim().length === 1
            ? "Keep typing — one character matches nothing."
            : list.term === ""
              ? "No patients yet. The first file a clinic creates is #00001."
              : `Nothing matches “${list.term}”.`}
      </p>
    ) : null;

  const rows = list.patients.map((patient) =>
    list.removed ? (
      // A removed file does not open — there is nothing to work on until it is
      // back. The row is the restore instead.
      <div key={patient.id} className={styles.removedRow} data-testid="removed-row">
        <span className={styles.rowName}>
          {patient.firstName} {patient.lastName}
        </span>
        <span className={styles.rowMeta}>{patient.fileNumberDisplay}</span>
        <button
          type="button"
          className={styles.restore}
          data-testid="restore-patient"
          onClick={() => {
            void restorePatient(patient.id).then((restored) => {
              list.refresh();
              navigate(`/patients/${restored.id}`);
            });
          }}
        >
          Put it back
        </button>
      </div>
    ) : (
      <button
        key={patient.id}
        type="button"
        className={patient.id === activeId ? `${styles.row} ${styles.rowActive}` : styles.row}
        aria-current={patient.id === activeId ? "page" : undefined}
        onClick={() => openFile(patient.id)}
        data-ring="patients.row"
        data-ring-id={patient.id}
        data-testid="patient-row"
        data-card=""
      >
        <span className={styles.rowName} data-testid="patient-row-name">
          {patient.firstName} {patient.lastName}
        </span>
        <span className={styles.rowMeta}>{patient.fileNumberDisplay}</span>
        {mode === "page" && (
          <span className={styles.rowMeta}>{formatPhone(patient.phoneE164)}</span>
        )}
        {/*
          **Only while sorting by it** (Anthony's QA, item 19). A number on
          every row all the time is a judgement on every patient the
          receptionist did not ask for; shown when the list is ordered by it,
          it is the list explaining itself.
        */}
        {attendance && <AttendanceMeta patient={patient} />}
      </button>
    ),
  );

  const pager = (
    <Pager
      page={list.page}
      count={pageSize === 0 ? 0 : Math.ceil(list.total / pageSize)}
      onPage={list.setPage}
      from={list.page * pageSize + 1}
      to={list.page * pageSize + list.patients.length}
      total={list.total}
      noun={list.removed ? "removed files" : "patients"}
      testId="patient-pager"
    />
  );

  const search = (
    <input
      className={styles.search}
      type="search"
      value={list.term}
      onChange={(event) => list.setTerm(event.target.value)}
      placeholder="Name, phone or file number…"
      aria-label="Search patients"
      data-testid="patient-search"
    />
  );

  const sorter = (
    <span className={styles.sortWrap}>
      <select
        className={styles.sort}
        value={sort}
        onChange={(event) => setSort(event.target.value as PatientSort | "")}
        aria-label="Sort patients"
        data-testid="patient-sort"
      >
        <option value="">Sort: Best match</option>
        <option value="name">Sort: Name (A–Z)</option>
        <option value="fileNumber">Sort: File number</option>
        <option value="recent">Sort: Most recently added</option>
        {/* Anthony's QA, item 19 — most reliable first, the way round he asked. */}
        <option value="attendance">Sort: Best attendance</option>
      </select>
    </span>
  );

  /*
    QA #38 — the way back from a removal. Admin only, matching the server, and
    hidden entirely for everyone else rather than shown disabled.
  */
  const removedToggle = can("patients.delete") ? (
    <button
      type="button"
      className={styles.quietButton}
      aria-pressed={list.removed}
      onClick={() => list.setRemoved(!list.removed)}
      data-testid="toggle-removed"
    >
      {list.removed ? "← Back to the patient list" : "Removed files"}
    </button>
  ) : null;

  if (mode === "drawer") {
    return (
      <div className={styles.drawerInner}>
        <div className={styles.drawerHead}>
          <div className={styles.drawerTitle}>
            <h2>{list.removed ? "Removed files" : "Patients"}</h2>
            {!list.removed && (
              <button
                type="button"
                className={styles.newSmall}
                onClick={onNew}
                data-testid="new-patient"
              >
                + New
              </button>
            )}
            <button
              type="button"
              className={styles.close}
              aria-label="Close the patient list"
              onClick={onClose}
              data-testid="patient-drawer-close"
            >
              ×
            </button>
          </div>
          {!list.removed && (
            <div className={styles.drawerSearch}>
              {search}
              {sorter}
            </div>
          )}
        </div>
        <div className={styles.drawerList} ref={areaRef} data-testid="patient-list">
          {list.error !== null && <p className={styles.note}>{list.error}</p>}
          {empty}
          {rows}
        </div>
        <div className={styles.drawerFoot}>
          {removedToggle}
          {pager}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.listPage}>
      <PatientsPageRing
        total={list.total}
        term={list.term}
        removed={list.removed}
        onRemoved={can("patients.delete") ? list.setRemoved : undefined}
        onNew={onNew}
      />
      <header className={styles.listHead}>
        <h1 className={styles.title}>{list.removed ? "Removed files" : "Patients"}</h1>
        <div className={styles.listActions}>
          {administrator && !list.removed && (
            <button
              type="button"
              className={styles.quietButton}
              onClick={() => setDuplicates(true)}
              data-testid="open-duplicates"
            >
              Possible duplicates
            </button>
          )}
          {removedToggle}
          {!list.removed && (
            <button
              type="button"
              className={styles.newPatient}
              onClick={onNew}
              data-testid="new-patient"
            >
              + New patient
            </button>
          )}
        </div>
      </header>

      {duplicates && <DuplicatesModal onClose={() => setDuplicates(false)} />}

      {!list.removed && (
        <div className={styles.listSearch}>
          {search}
          {sorter}
        </div>
      )}

      <div className={attendance ? `${styles.table} ${styles.withAttendance}` : styles.table}>
        <div className={styles.tableHead} aria-hidden="true">
          <span>Name</span>
          <span>File</span>
          <span>{list.removed ? "" : "Phone"}</span>
          {attendance && <span>Attendance</span>}
        </div>
        <div className={styles.tableBody} ref={areaRef} data-testid="patient-list">
          {list.error !== null && <p className={styles.note}>{list.error}</p>}
          {empty}
          {rows}
        </div>
      </div>

      <div className={styles.listFoot}>{pager}</div>
    </div>
  );
}

/**
 * **The Patients page's own ring** (`page.patients`, STORY-221) — blank space
 * on the list page, not the drawer, whose blank space is the file's. Its two
 * actions are the page's two buttons: *New patient*, and *Removed files* (or
 * the way back from them), which is admin only as the button is.
 */
function PatientsPageRing({
  total,
  term,
  removed,
  onRemoved,
  onNew,
}: {
  total: number;
  term: string;
  removed: boolean;
  /** Absent without `patients.delete`, as the button is. */
  onRemoved?: (removed: boolean) => void;
  onNew: () => void;
}): null {
  useRingProvider("page", () => ({
    ring: "page.patients",
    title: removed ? "Removed files" : "Patients",
    meta: removed
      ? counted(total, "removed file")
      : term.trim() === ""
        ? counted(total, "file")
        : `${counted(total, "file")} matching`,
    actions: [
      !removed && {
        key: "newp",
        label: "New patient",
        icon: RING_ICONS.personplus,
        hint: "Opens a new file",
        needs: ["patients.create"],
        run: onNew,
      },
      onRemoved !== undefined && {
        key: "removed",
        label: removed ? "Back to the patient list" : "Removed files",
        icon: RING_ICONS.folder,
        hint: removed ? "The live list again" : "Files that were removed",
        needs: ["patients.delete"],
        run: () => onRemoved(!removed),
      },
    ],
  }));
  return null;
}

/**
 * The list's state and its requests: the search term (seeded from `?q=`), the
 * Removed files switch and the page — **numbered pages**, fetched by offset
 * with the server's count (PF4). A page is `pageSize` rows, worked out from the
 * height the list has; nothing is fetched until that is known.
 */
function usePatientList(
  pageSize: number,
  sort: PatientSort | "",
): {
  term: string;
  setTerm: (term: string) => void;
  removed: boolean;
  setRemoved: (removed: boolean) => void;
  page: number;
  setPage: (page: number) => void;
  patients: PatientSearchResult[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [searchParams] = useSearchParams();

  /**
   * Seeded from `?q=`, so the banner's search box lands somewhere (QA item 4).
   * **Read, never written back** — pushing every keystroke into the URL would
   * fill the back button with one entry per letter.
   */
  const queryFromUrl = searchParams.get("q") ?? "";
  const [term, setTerm] = useState(queryFromUrl);
  useEffect(() => {
    setTerm(queryFromUrl);
  }, [queryFromUrl]);

  /**
   * Browsing removed files instead of live ones (QA #38) — **instead of** the
   * live list rather than mixed into it: a removed file appearing in an
   * ordinary search is how somebody books an appointment against one.
   */
  const [removed, setRemoved] = useState(false);
  const [page, setPage] = useState(0);
  const [patients, setPatients] = useState<PatientSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  // A new search, sort or list starts at the first page.
  useEffect(() => {
    setPage(0);
  }, [term, sort, removed]);

  /**
   * A different page size keeps the first row on screen rather than the page
   * number — a window made shorter should not jump somebody forwards.
   */
  const previousSize = useRef(pageSize);
  useEffect(() => {
    const before = previousSize.current;
    previousSize.current = pageSize;
    if (before > 0 && pageSize > 0 && before !== pageSize) {
      setPage((current) => Math.floor((current * before) / pageSize));
    }
  }, [pageSize]);

  // Rising sequence number, so a slow response for an old term cannot overwrite
  // a fast one for the current term.
  const latest = useRef(0);

  useEffect(() => {
    if (pageSize <= 0) return;
    const controller = new AbortController();
    const sequence = latest.current + 1;
    latest.current = sequence;

    // Debounced, so typing a surname is one query rather than eight.
    const timer = setTimeout(() => {
      setLoading(true);
      searchPatients(
        {
          q: term,
          limit: pageSize,
          offset: page * pageSize,
          ...(sort === "" ? {} : { sort }),
          ...(removed ? { removed: true } : {}),
        },
        controller.signal,
      )
        .then((result) => {
          if (sequence !== latest.current) return;
          setPatients(result.patients);
          setTotal(result.total);
          setError(null);
        })
        .catch((cause: unknown) => {
          if (controller.signal.aborted || sequence !== latest.current) return;
          setError(cause instanceof Error ? cause.message : "Could not reach the server.");
        })
        .finally(() => {
          if (sequence === latest.current) setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term, sort, removed, page, pageSize, version]);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  return {
    term,
    setTerm,
    removed,
    setRemoved,
    page,
    setPage,
    patients,
    total,
    loading,
    error,
    refresh,
  };
}

/**
 * How reliably one patient turns up.
 *
 * **"No history" is a real answer and gets said** (Anthony's QA, item 19). A
 * patient registered this morning has neither kept nor missed anything, and
 * printing 0% would libel them while 100% would recommend them. The count sits
 * beside the percentage because the percentage alone misleads at small numbers.
 */
function AttendanceMeta({ patient }: { patient: PatientSearchResult }): React.JSX.Element {
  const concluded = patient.attendedCount + patient.noShowCount;

  if (patient.attendanceRate === null) {
    return (
      <span className={styles.rowMeta} data-testid="patient-attendance">
        No appointment history yet
      </span>
    );
  }

  return (
    <span className={styles.rowMeta} data-testid="patient-attendance">
      {Math.round(patient.attendanceRate * 100)}% attended · {patient.attendedCount} of {concluded}
      {patient.noShowCount > 0 && ` · ${String(patient.noShowCount)} missed`}
    </span>
  );
}
